import React from "react";
import { observer } from "mobx-react-lite";
import clsx from "clsx";
import { IconChevronRight } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import LoadingIcon from "components/LoadingIcon";
import { PropertyBrowserNodeOUT } from "api-client";
import { usePropertyBrowserStore } from "./PropertyBrowserStoreContext";

export function useNodeLabel(node: PropertyBrowserNodeOUT | undefined) {
  const { i18n } = useTranslation();
  if (!node) return null;
  return i18n.language === "en" ? node.name : node.name_de ?? node.name;
}

const PropertyBrowserNodeRow = observer(function PropertyBrowserNodeRow({
  node,
  onNavigate,
  onSelect,
  filter,
}: {
  node: PropertyBrowserNodeOUT;
  onNavigate: (node: PropertyBrowserNodeOUT) => void;
  onSelect: (node: PropertyBrowserNodeOUT) => void;
  filter?: (node: PropertyBrowserNodeOUT) => boolean;
}) {
  const store = usePropertyBrowserStore();
  const label = useNodeLabel(node);

  if (filter && !filter(node)) return null;

  const isGroup = node.has_children;
  const isLoading = store.loadingNodes.has(node.node_id);

  return (
    <div
      className={clsx(
        "flex items-center justify-between px-4 py-2 text-sm font-medium cursor-pointer select-none",
        isGroup
          ? "text-gray-700 hover:bg-gray-50"
          : "text-gray-700 hover:bg-indigo-50 hover:text-indigo-700"
      )}
      onClick={() => (isGroup ? onNavigate(node) : onSelect(node))}
    >
      <span className="truncate">{label}</span>
      {isGroup && (
        <span className="text-gray-400 shrink-0 ml-2">
          {isLoading ? (
            <LoadingIcon type="gray" className="w-4 h-4" />
          ) : (
            <IconChevronRight size={20} />
          )}
        </span>
      )}
    </div>
  );
});

export default PropertyBrowserNodeRow;
